import type FaqLocal from "~/libs/interfaces/FaqLocal";
import type FaqFilters from "~/libs/interfaces/FaqFilters";
import NotFoundError from "~/libs/errors/NotFoundError";
import type {BaseApiFetchOptions} from "~/libs/interfaces/BaseApiFetchOptions";
import type FaqStrapi from "~/libs/interfaces/FaqStrapi";
import faqLocalFactory from "~/libs/factories/FaqLocalFactory";

const faqStore = (type: string = 'faq', key?: string) => defineStore(`faq-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      faqs: FaqLocal[]
      faq: Ref<FaqLocal | null>
      filters: FaqFilters
    } = {
      isLoading: ref(true),
      limit: ref(10),
      faqs: reactive([]),
      faq: ref(null),
      filters: {},
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    }
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    /**
     * Get items with applied filters and presets
     * @param offset
     * @param limit
     * @return FaqLocal[]
     */
    getFilteredFaqs(offset: number = 0, limit: number = 10): FaqLocal[] {
      let matchedCount = 0

      const faqs = this.faqs.map((faq) => {
        let matchFilters = true

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...faq,
          isEnabled: matchFilters
        }
      })
      return faqs
    },

    /**
     * Get only filtered items (remove non visible)
     * @param offset
     * @param limit
     */
    getOnlyFilteredFaqs(offset: number = 0, limit: number = 10): FaqLocal[] {
      const items = this.getFilteredFaqs(offset, limit)
      return items.filter((item) => item.isVisible)
    },

    /**
     * Get count of getFilteredFaqs
     * @return number
     */
    getFilteredFaqsCount(): number {
      const items = this.getOnlyFilteredFaqs(0, this.faqs.length).filter(item => item.isVisible)
      return items?.length || 0
    },

    /**
     * Get count of items
     * @return number
     */
    getFaqsCount(): number {
      return this.faqs.length || 0
    },
    /**
     * Get faq by id
     * @param id
     */
    getFaq(id: string|number): FaqLocal | undefined {
      const faq = this.faqs.find((item) => {
        return String(item.id) === String(id)
      })
      return faq
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: FaqFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check item ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      return false
    },

    checkFiltersChanges(filters: FaqFilters): void {
      // reset items if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.faqs = reactive([])
        this.faq = null
      }
    },

    /**
     * Fetch items from server
     * @return {Promise<void>}
     */
    async fetchFaqs(
      filters: FaqFilters = {},
      options: BaseApiFetchOptions = {}
    ): Promise<void> {
      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()
      const { find } = useStrapi()

      const settings = {
        forceRefetch: false,
        checkFiltersChange: true,
        ...options
      }

      // reset items if filter changed
      if (settings.checkFiltersChange) {
        this.checkFiltersChanges(filters)
      }

      // no need to fetch items
      if (!settings.forceRefetch && this.faqs.length >= this.limit && !persistedStore.needSync(this.$id)) {
        return
      }

      this.isLoading = true

      const apiFilters = {
        ...(filters.ids && {
          documentId: {
            $in: filters.ids,
          }
        }),
        isVisible: {
          $eq: true
        }
      }

      const params = {
        filters: apiFilters,
        sort: "order",
      }

      try {
        const result = await find<FaqStrapi>('faqs', params)
        //@ts-ignore
        const items = result.data || []
        this.processData(items, this.limit === 1)
        persistedStore.setLastSync(this.$id)
        this.isLoading = false
      } catch (e) {
        logger?.error('useFaqsStore:fetchFaqs - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },
    /**
     * Process loaded data from Strapi
     * @param newItems
     * @param isDetail
     */
    processData(newItems: FaqStrapi[], isDetail: boolean = false):void {
      if (newItems.length) {
        // check item detail load
        if (isDetail && newItems[0]) {
          this.setItem(faqLocalFactory.createFaqFromStrapi(newItems[0]))
        } else if (isDetail && (!newItems || !newItems.length)) {
          throw new NotFoundError('Faq not found')
        }
        if (newItems && newItems.length) {
          // add new items to list
          this.addItems(newItems.map(faqLocalFactory.createFaqFromStrapi).filter(item => !!item), true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Faq not found')
      }
    },
    /**
     * Add items to store - remove duplicities
     * @param newItems
     * @param rewrite
     */
    addItems(newItems?: FaqLocal[], rewrite: boolean = false): void {
      newItems?.forEach((newItem) => {
        const foundIndex = this.faqs.findIndex(faq => faq.id === newItem.id)
        if (foundIndex >= 0 && rewrite) {
          this.faqs.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.faqs.push(newItem)
        }
      })
    },

    setItem(item?: FaqLocal): void {
      if (item) {
        this.faq = item
      } else {
        this.faq = null
      }
    }

  }
})

export const useFaqsStore = (type?: string, key?: string) => {
  return faqStore(type, key)()
}
